import { useEffect, useState } from 'react';
import { useApi } from '../../lib/ApiProvider';
import type {
  PhotoPolicyDocumentType,
  PhotoPolicyVersionDto,
  PhotoRequirementDto,
} from '../../lib/api';
import { toDisplayError } from '../../lib/errors';
import { Alert } from '../../components/ui/Alert';
import { Button } from '../../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';

const TYPES: { key: PhotoPolicyDocumentType; label: string }[] = [
  { key: 'PV_BATI', label: 'PV — Mise en valeur' },
  { key: 'PV_VALEUR_REELLE', label: 'PV — Valeur réelle' },
  { key: 'PV_EXPERTISE_COMPLETE', label: 'PV — Valeur vénale' },
  { key: 'RAPPORT_EXPERTISE', label: "Rapport d'expertise" },
  { key: 'DOCUMENT_METHODE_SPECIFIQUE', label: 'Document méthode spécifique' },
];

type Row = { photosRequired: boolean; minimumPhotoCount: number };
const DEFAULT_ROW: Row = { photosRequired: false, minimumPhotoCount: 1 };

function rowsFromPolicy(policy: PhotoPolicyVersionDto | null): Record<string, Row> {
  const out: Record<string, Row> = {};
  for (const t of TYPES) {
    const r = policy?.requirements.find((x) => x.documentType === t.key);
    out[t.key] = r
      ? { photosRequired: r.photosRequired, minimumPhotoCount: r.minimumPhotoCount }
      : { ...DEFAULT_ROW };
  }
  return out;
}

/**
 * A-1 — Configuration › Photographies obligatoires. Une ligne par type de document ; case à cocher ;
 * champ « nombre minimal » visible uniquement si cochée ; bouton Enregistrer (crée une NOUVELLE
 * version côté serveur) ; historique des versions. Toute règle métier reste côté serveur.
 */
export function PhotoPolicyPage(): JSX.Element {
  const api = useApi();
  const [rows, setRows] = useState<Record<string, Row>>(rowsFromPolicy(null));
  const [history, setHistory] = useState<PhotoPolicyVersionDto[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const reload = async (): Promise<void> => {
    try {
      const active = await api.getPhotoPolicy();
      setRows(rowsFromPolicy(active));
      setHistory(await api.photoPolicyHistory());
    } catch (e) {
      setError(toDisplayError(e).message);
    }
  };

  useEffect(() => {
    void reload();
  }, []);

  const setRow = (key: string, patch: Partial<Row>): void =>
    setRows((prev) => ({ ...prev, [key]: { ...prev[key]!, ...patch } }));

  const save = async (): Promise<void> => {
    setSaving(true);
    setError(null);
    try {
      const requirements: PhotoRequirementDto[] = TYPES.map((t) => ({
        documentType: t.key,
        photosRequired: rows[t.key]!.photosRequired,
        minimumPhotoCount: Math.max(1, rows[t.key]!.minimumPhotoCount),
      }));
      await api.savePhotoPolicy({ requirements });
      await reload();
    } catch (e) {
      setError(toDisplayError(e).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-4xl space-y-4">
      <h1 className="text-xl font-semibold text-slate-800">Photographies obligatoires</h1>
      {error ? <Alert variant="error">{error}</Alert> : null}
      <Card>
        <CardHeader>
          <CardTitle>Obligation par type de document</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {TYPES.map((t) => {
            const row = rows[t.key]!;
            return (
              <div key={t.key} className="flex items-center gap-4 border-b border-slate-100 py-2">
                <span className="w-64 text-sm text-slate-700">{t.label}</span>
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={row.photosRequired}
                    aria-label={`Photographies obligatoires — ${t.label}`}
                    onChange={(e) => setRow(t.key, { photosRequired: e.target.checked })}
                  />
                  Photographies obligatoires
                </label>
                {row.photosRequired ? (
                  <div className="flex items-center gap-2">
                    <Label htmlFor={`min-${t.key}`}>Nombre minimal</Label>
                    <Input
                      id={`min-${t.key}`}
                      aria-label={`min-${t.key}`}
                      type="number"
                      min={1}
                      className="w-20"
                      value={String(row.minimumPhotoCount)}
                      onChange={(e) =>
                        setRow(t.key, { minimumPhotoCount: Number(e.target.value) || 1 })
                      }
                    />
                  </div>
                ) : null}
              </div>
            );
          })}
          <div className="flex items-center gap-3 pt-2">
            <Button onClick={() => void save()} disabled={saving}>
              Enregistrer
            </Button>
            <button
              type="button"
              className="text-sm text-slate-500 underline"
              onClick={() => setShowHistory((v) => !v)}
            >
              {showHistory ? 'Masquer l’historique' : 'Historique des versions'}
            </button>
          </div>
        </CardContent>
      </Card>

      {showHistory ? (
        <Card>
          <CardHeader>
            <CardTitle>Historique</CardTitle>
          </CardHeader>
          <CardContent>
            {history.length === 0 ? (
              <p className="text-sm text-slate-500">Aucune version enregistrée.</p>
            ) : (
              <ul className="space-y-1 text-sm text-slate-700">
                {history.map((v) => (
                  <li key={v.id}>
                    v{v.version} — {v.status} — {new Date(v.createdAt).toLocaleString('fr-FR')}
                    {v.note ? ` — ${v.note}` : ''}
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
